import { RawDraftContentBlock, RawDraftEntity } from 'draft-js'
import { safeLink } from '../links'

export interface TextSegment {
  text: string
  bold: boolean
  italic: boolean
  /** Set when the segment is inside a LINK entity. */
  url: string | null
}

type EntityMap = { [key: string]: RawDraftEntity }

/**
 * Split a Draft.js block's text into runs that share the same formatting.
 *
 * Raw Draft.js ranges count Unicode code points (UnicodeUtils.strlen), not
 * UTF-16 units, so the text is split with Array.from before applying them --
 * otherwise an emoji earlier in the line would shift every range after it.
 */
export const segmentBlock = (
  block: Pick<
    RawDraftContentBlock,
    'text' | 'inlineStyleRanges' | 'entityRanges'
  >,
  entityMap: EntityMap = {}
): TextSegment[] => {
  const chars = Array.from(block.text)
  const bold = new Array<boolean>(chars.length).fill(false)
  const italic = new Array<boolean>(chars.length).fill(false)
  const url = new Array<string | null>(chars.length).fill(null)

  const eachIndex = (
    offset: number,
    length: number,
    fn: (i: number) => void
  ) => {
    const end = Math.min(chars.length, offset + length)
    for (let i = Math.max(0, offset); i < end; i++) fn(i)
  }

  block.inlineStyleRanges.forEach(({ offset, length, style }) => {
    if (style === 'BOLD') eachIndex(offset, length, i => (bold[i] = true))
    if (style === 'ITALIC') eachIndex(offset, length, i => (italic[i] = true))
  })

  block.entityRanges.forEach(({ offset, length, key }) => {
    const entity = entityMap[key]
    // Unsafe or unusable links render as plain text
    const href = entity?.type === 'LINK' ? safeLink(entity.data?.url) : null
    if (href) eachIndex(offset, length, i => (url[i] = href))
  })

  const segments: TextSegment[] = []
  chars.forEach((char, i) => {
    const last = segments[segments.length - 1]
    if (
      last &&
      last.bold === bold[i] &&
      last.italic === italic[i] &&
      last.url === url[i]
    ) {
      last.text += char
    } else {
      segments.push({
        text: char,
        bold: bold[i],
        italic: italic[i],
        url: url[i]
      })
    }
  })
  return segments
}
